import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client';
import { Preloader, Pagination, NumberInput } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { EventLogsTable, EventsFilter, LogMobile } from 'components';
import { DEFAULT_EVENTS_TYPE_FILTER } from 'consts';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import {
  IGetEventsRequest,
  GET_EVENT_LIST,
  IGetEventListResponse,
} from 'schemas';
import { IEventsFilter, ELogType } from 'types';

import { IFormValues } from './interfaces';
import s from './Logs.module.scss';
import { GET_WORKSHIFT_ID, IGetWorkShiftIdResponse } from './schema';

export const Logs: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { me, isActiveOperator, permissions },
  } = useContext(AuthContext);

  const { data: workshiftIdRequest } = useQuery<IGetWorkShiftIdResponse>(
    GET_WORKSHIFT_ID,
    {
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
      skip: !permissions?.has('work_shift_active'),
    }
  );

  const [isOperatorLogs, setIsOperatorLogs] = useState(isActiveOperator);
  const [isActiveRegister, setIsActiveRegister] = useState<boolean>(false);

  const workshiftId = useMemo(
    () => workshiftIdRequest?.getWorkShiftPaymentOverviewData?.id,
    [workshiftIdRequest]
  );

  const [filters, setFilters] = useState<IEventsFilter | null>(null);
  const [pagesInfo, setPagesInfo] = useState({ currentPage: 1, lastPage: 1 });
  const [isPaginationVisible, setIsPaginationVisible] = useState(false);

  const [getEventList, { data: loadedEventList, loading, called, variables }] =
    useLazyQuery<IGetEventListResponse>(GET_EVENT_LIST, {
      fetchPolicy: 'no-cache',
      onCompleted: ({ eventList }) => {
        if (!eventList) return;

        setPagesInfo({ ...eventList.paginatorInfo });
      },
    });

  const getEventsForType = useCallback(
    (variables: IGetEventsRequest) => {
      if (!variables.input) return;

      return getEventList({
        variables,
      });
    },
    [getEventList]
  );

  const handlePageChange = (page: number): void => {
    if (page !== currentPage) {
      setPagesInfo((prev) => ({ ...prev, currentPage: page }));
      getEventsForType({
        input: variables?.input,
        page,
      });
    }
  };

  useEffect(() => {
    if (!me) return;
    if (!filters) return;

    if (isActiveOperator && !workshiftId) return;

    const input = {
      ...variables?.input,
      q: filters?.q || undefined,
      types: filters?.types.length ? filters.types : DEFAULT_EVENTS_TYPE_FILTER,
      start: filters?.from || undefined,
      finish: filters?.to || undefined,
    };

    if (isOperatorLogs) {
      input.work_shift_id = workshiftId;
    } else {
      input.work_shift_id = undefined;
    }

    const timerId = setTimeout(() => {
      return getEventsForType({ input });
    }, 300);

    return () => clearTimeout(timerId);
  }, [
    filters?.q,
    filters?.types,
    filters?.from,
    filters?.to,
    isOperatorLogs,
    workshiftId,
    me,
    isActiveRegister,
  ]);

  const paginationTopRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollEl = document.querySelector('main');
    const handleScroll = (): (() => void) | undefined => {
      if (!paginationTopRef.current) return;

      const { bottom } = paginationTopRef.current.getBoundingClientRect();
      const isOutOfView = bottom < 70;
      setIsPaginationVisible(isOutOfView);
    };

    scrollEl?.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => scrollEl?.removeEventListener('scroll', handleScroll);
  }, [paginationTopRef]);

  const eventList = loadedEventList?.eventList.data ?? [];
  const { lastPage, currentPage } = pagesInfo;

  const { control, handleSubmit } = useForm<IFormValues>({
    defaultValues: {
      search: '',
    },
  });

  const size = useWindowResize();

  return (
    <div className={s.container} ref={containerRef}>
      <div ref={paginationTopRef}>
        <EventsFilter
          isLogs={true}
          setFilters={setFilters}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          lastPage={lastPage}
          setIsOperatorLogs={setIsOperatorLogs}
          isOperatorLogs={isOperatorLogs}
          isActiveOperator={isActiveOperator}
          isActiveRegister={isActiveRegister}
          setIsActiveRegister={setIsActiveRegister}
        />
      </div>
      <Preloader
        loading={loading || !called}
        hasData={eventList.length > 0}
        noDataPlaceholder={t('logs.placeholder')}
      >
        {size.width < 960 ? (
          <LogMobile
            eventLogs={
              isOperatorLogs
                ? eventList.filter(
                    ({ type }) => type !== ELogType.SETTINGS_UPDATED
                  )
                : eventList
            }
          />
        ) : (
          <div className={s.events_block}>
            <div className={s.events}>
              <EventLogsTable
                eventLogs={
                  isOperatorLogs
                    ? eventList.filter(
                        ({ type }) => type !== ELogType.SETTINGS_UPDATED
                      )
                    : eventList
                }
                bottomRadius={currentPage !== 100}
              />
              {currentPage === 100 && (
                <div className={s.max_placeholder}>
                  {t('logs.max_placeholder')}
                </div>
              )}
            </div>
          </div>
        )}
        {lastPage > 1 && (
          <div
            className={cn(
              s.pagination_bottom,
              isPaginationVisible && s.visible
            )}
          >
            <Pagination
              onChange={(page: number) => {
                if (page != pagesInfo.currentPage) {
                  handlePageChange(page);
                }
              }}
              lastPage={pagesInfo.lastPage}
              currentPage={pagesInfo.currentPage}
              className={s.pagination}
              isChose={true}
            />
            {pagesInfo && (
              <form
                onSubmit={handleSubmit(({ page }) =>
                  handlePageChange(page || 1)
                )}
              >
                <Controller
                  name="page"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <NumberInput
                      value={value || 1}
                      onChange={(val) => {
                        if (typeof val === 'number') {
                          if (val < 1) {
                            onChange(1);
                          } else if (val > pagesInfo?.lastPage) {
                            onChange(pagesInfo?.lastPage);
                          } else {
                            onChange(val);
                          }
                        } else {
                          onChange('');
                        }
                      }}
                      variant="secondary"
                      placeholder="№"
                      className={s.pagination_input}
                    />
                  )}
                />
                <button type="submit" style={{ display: 'none' }} />
              </form>
            )}
          </div>
        )}
      </Preloader>
    </div>
  );
};
